import { Component, ChangeDetectorRef, OnDestroy, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { Header } from '../../components/header/header';
import { Footer } from '../../components/footer/footer';
import cloudflareConfig from '../../core/config/cloudflare.config';
import { MetaService } from '../../core/services/meta.service';

declare global {
  interface Window {
    turnstile: any;
  }
}

@Component({
  selector: 'app-forgot-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, Header, Footer],
  template: `
    <app-header />

    <div class="min-h-screen bg-gray-50 flex items-start justify-center pt-8 pb-12 px-4 sm:px-6 lg:px-8">
      <div class="max-w-md w-full space-y-8">
        <div>
          <h2 class="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Recuperar contrasena
          </h2>
          <p class="mt-2 text-center text-sm text-gray-600">
            Te enviaremos un enlace si el email esta registrado.
          </p>
        </div>

        <form class="mt-8 space-y-6" [formGroup]="form" (ngSubmit)="onSubmit()">
          <div class="rounded-md shadow-sm space-y-4">
            <div>
              <label for="email" class="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                formControlName="email"
                class="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-[#e8343f] focus:border-[#e8343f] focus:z-10 sm:text-sm"
                [class.border-red-500]="isFieldInvalid('email')"
                placeholder="tu@email.com"
              />
              @if (isFieldInvalid('email')) {
                <p class="mt-1 text-sm text-red-600">Email invalido</p>
              }
            </div>
          </div>

          @if (errorMessage) {
            <div class="rounded-md bg-red-50 p-4">
              <p class="text-sm font-medium text-red-800">{{ errorMessage }}</p>
            </div>
          }

          @if (successMessage) {
            <div class="rounded-md bg-green-50 p-4">
              <p class="text-sm font-medium text-green-800">{{ successMessage }}</p>
            </div>
          }

          <div class="flex justify-center">
            <div id="turnstile-container-forgot"></div>
          </div>

          <button
            type="submit"
            [disabled]="isLoading || !turnstileToken"
            class="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-[#e8343f] hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#e8343f] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            @if (isLoading) {
              Enviando...
            } @else {
              Enviar enlace
            }
          </button>

          <div class="text-center text-sm">
            <a routerLink="/login" class="font-medium text-[#e8343f] hover:text-red-700">
              Volver a iniciar sesion
            </a>
          </div>
        </form>
      </div>
    </div>

    <app-footer />
  `,
  styles: []
})
export class ForgotPasswordComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private fb = inject(FormBuilder);
  private platformId = inject(PLATFORM_ID);
  private cdr = inject(ChangeDetectorRef);
  private metaService = inject(MetaService);
  private isBrowser: boolean;

  form!: FormGroup;
  isLoading = false;
  errorMessage = '';
  successMessage = '';

  turnstileToken: string | null = null;
  turnstileWidgetId: string | null = null;
  turnstileSiteKey: string = cloudflareConfig.turnstile.Site_Key;

  constructor() {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    this.metaService.resetToDefault();

    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    });

    if (this.isBrowser) {
      this.waitForTurnstile();
    }
  }

  ngOnDestroy(): void {
    if (this.isBrowser && this.turnstileWidgetId && window.turnstile) {
      try {
        window.turnstile.remove(this.turnstileWidgetId);
      } catch (error) {
        console.error('Error al limpiar Turnstile:', error);
      }
    }
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (!this.turnstileToken) {
      this.errorMessage = 'Por favor complete la verificacion de seguridad';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.authService.requestPasswordReset({
      email: this.form.value.email,
      turnstileToken: this.turnstileToken
    }).subscribe({
      next: (response) => {
        if (response.success) {
          this.successMessage = response.data?.message || response.message || 'Si el email esta registrado, recibiras un enlace.';
        } else {
          this.errorMessage = response.message || 'No se pudo solicitar la recuperacion.';
        }
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.errorMessage = error.error?.message || 'No se pudo solicitar la recuperacion.';
        this.isLoading = false;
        this.resetTurnstile();
      }
    });
  }

  private waitForTurnstile(): void {
    if (!this.isBrowser) return;

    if (window.turnstile) {
      this.renderTurnstile();
      return;
    }

    let attempts = 0;
    const maxAttempts = 50;
    const checkInterval = setInterval(() => {
      attempts++;
      if (window.turnstile) {
        clearInterval(checkInterval);
        this.renderTurnstile();
      } else if (attempts >= maxAttempts) {
        clearInterval(checkInterval);
        this.errorMessage = 'Error al cargar el sistema de verificacion';
      }
    }, 100);
  }

  private renderTurnstile(): void {
    if (!this.isBrowser || !window.turnstile) return;

    const container = document.getElementById('turnstile-container-forgot');
    if (!container) return;

    try {
      this.turnstileWidgetId = window.turnstile.render('#turnstile-container-forgot', {
        sitekey: this.turnstileSiteKey,
        theme: 'light',
        callback: (token: string) => {
          this.turnstileToken = token;
          this.cdr.detectChanges();
        },
        'error-callback': () => {
          this.turnstileToken = null;
          this.cdr.detectChanges();
        },
        'expired-callback': () => {
          this.turnstileToken = null;
          this.cdr.detectChanges();
        }
      });
    } catch (error) {
      console.error('Error al renderizar Turnstile:', error);
    }
  }

  private resetTurnstile(): void {
    if (!this.isBrowser || !this.turnstileWidgetId || !window.turnstile) return;

    try {
      window.turnstile.reset(this.turnstileWidgetId);
      this.turnstileToken = null;
    } catch (error) {
      console.error('Error al resetear Turnstile:', error);
    }
  }
}
